/**
 * Python's arithmetic and string semantics, exactly, where the layout depends on them.
 *
 * The layout was written in Python and every pixel it places passes through
 * `round()`, float `//`, `sum()` or `str.split()`. JavaScript's nearest
 * equivalents differ in ways that move a caption by a pixel: `Math.round`
 * rounds halves up where Python rounds them to even, `sum()` has compensated
 * for rounding error since Python 3.12, and `\s` is not Python's whitespace.
 * These functions reproduce Python 3.13 bit for bit, so the TypeScript layout
 * can be held to the Python one byte for byte.
 */

const view = new DataView(new ArrayBuffer(8));

/** A finite double as sign · mantissa · 2^exponent, all exact. */
function decompose(value: number): { negative: boolean; mantissa: bigint; exponent: number } {
  view.setFloat64(0, value);
  const high = view.getUint32(0);
  const low = view.getUint32(4);
  const negative = high >>> 31 === 1;
  const biased = (high >>> 20) & 0x7ff;
  const fraction = (BigInt(high & 0xfffff) << 32n) | BigInt(low);
  if (biased === 0) return { negative, mantissa: fraction, exponent: -1074 };
  return { negative, mantissa: fraction | (1n << 52n), exponent: biased - 1075 };
}

function requireFinite(value: number, operation: string): void {
  if (!Number.isFinite(value)) throw new RangeError(`${operation} of ${value}`);
}

/** |value| · 10^digits rounded half to even on the exact binary value, as an integer. */
function scaledHalfEven(value: number, digits: number): bigint {
  const { mantissa, exponent } = decompose(value);
  let numerator = mantissa * 10n ** BigInt(digits);
  let denominator = 1n;
  if (exponent >= 0) numerator <<= BigInt(exponent);
  else denominator <<= BigInt(-exponent);
  let quotient = numerator / denominator;
  const twice = 2n * (numerator % denominator);
  if (twice > denominator || (twice === denominator && quotient % 2n === 1n)) quotient += 1n;
  return quotient;
}

/** `round(x)`: to the nearest integer, halves to even. */
export function round(value: number): number {
  requireFinite(value, "round");
  const floor = Math.floor(value);
  const difference = value - floor;
  if (difference < 0.5) return floor + 0;
  if (difference > 0.5) return floor + 1;
  return floor % 2 === 0 ? floor + 0 : floor + 1;
}

/** `round(x, digits)`: correctly rounded to `digits` decimals, halves to even. */
export function roundTo(value: number, digits: number): number {
  requireFinite(value, "round");
  if (!Number.isInteger(digits) || digits < 0) throw new RangeError(`digits ${digits}`);
  const scaled = scaledHalfEven(value, digits);
  const magnitude = Number(`${scaled}e-${digits}`);
  return value < 0 || Object.is(value, -0) ? -magnitude : magnitude;
}

/** `f"{x:.{digits}f}"`. */
export function fixed(value: number, digits: number): string {
  requireFinite(value, "format");
  const scaled = scaledHalfEven(value, digits).toString().padStart(digits + 1, "0");
  const whole = scaled.slice(0, scaled.length - digits);
  const fraction = scaled.slice(scaled.length - digits);
  const sign = value < 0 || Object.is(value, -0) ? "-" : "";
  return digits === 0 ? `${sign}${whole}` : `${sign}${whole}.${fraction}`;
}

/** `a // b` for floats: CPython's float_floor_div, including its correction step. */
export function floorDiv(dividend: number, divisor: number): number {
  if (divisor === 0) throw new RangeError("float floor division by zero");
  let modulo = dividend % divisor;
  let quotient = (dividend - modulo) / divisor;
  if (modulo !== 0) {
    if (divisor < 0 !== modulo < 0) {
      modulo += divisor;
      quotient -= 1;
    }
  }
  if (quotient !== 0) {
    let floored = Math.floor(quotient);
    if (quotient - floored > 0.5) floored += 1;
    return floored;
  }
  return dividend / divisor < 0 || Object.is(dividend / divisor, -0) ? -0 : 0;
}

/** `int(x)` for a float: truncation toward zero. */
export function int(value: number): number {
  requireFinite(value, "int");
  return Math.trunc(value) + 0;
}

/**
 * `sum()` of floats, as Python 3.12+ computes it: Neumaier's compensated
 * summation, starting from the integer 0. An integer total is exact either way.
 */
export function sum(values: Iterable<number>): number {
  let total = 0;
  let compensation = 0;
  let first = true;
  for (const value of values) {
    if (first) {
      total = 0 + value;
      first = false;
      continue;
    }
    const next = total + value;
    if (Math.abs(total) >= Math.abs(value)) compensation += total - next + value;
    else compensation += value - next + total;
    total = next;
  }
  if (compensation !== 0 && Number.isFinite(compensation)) total += compensation;
  return total;
}

/** The characters `str.isspace()` accepts. */
const WHITESPACE = String.fromCharCode(
  0x0009, 0x000a, 0x000b, 0x000c, 0x000d, 0x001c, 0x001d, 0x001e, 0x001f, 0x0020, 0x0085, 0x00a0, 0x1680, 0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200a, 0x2028, 0x2029, 0x202f, 0x205f, 0x3000,
);
const WHITESPACE_RUN = new RegExp(`[${WHITESPACE}]+`, "u");

export function isSpace(character: string): boolean {
  return character.length > 0 && [...character].every((c) => WHITESPACE.includes(c));
}

/** `str.split()` with no separator: runs of Python whitespace, no empty strings. */
export function split(text: string): string[] {
  return text.split(WHITESPACE_RUN).filter((part) => part.length > 0);
}

/** `str.strip(chars)`; with no `chars`, Python whitespace. */
export function strip(text: string, chars?: string): string {
  return rstrip(lstrip(text, chars), chars);
}

export function lstrip(text: string, chars?: string): string {
  const set = chars ?? WHITESPACE;
  const points = [...text];
  let start = 0;
  while (start < points.length && set.includes(points[start] ?? "")) start += 1;
  return points.slice(start).join("");
}

export function rstrip(text: string, chars?: string): string {
  const set = chars ?? WHITESPACE;
  const points = [...text];
  let end = points.length;
  while (end > 0 && set.includes(points[end - 1] ?? "")) end -= 1;
  return points.slice(0, end).join("");
}

/** `len(s)`: code points, not UTF-16 units. */
export function len(text: string): number {
  let count = 0;
  for (const _ of text) count += 1;
  return count;
}

/** `s.endswith(suffixes)` for a tuple of suffixes. */
export function endsWithAny(text: string, suffixes: readonly string[]): boolean {
  return suffixes.some((suffix) => text.endsWith(suffix));
}

const NON_PRINTABLE = /[\p{C}\p{Z}]/u;

function stringRepr(text: string): string {
  const quote = text.includes("'") && !text.includes('"') ? '"' : "'";
  let out = quote;
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0;
    if (character === quote || character === "\\") out += `\\${character}`;
    else if (character === "\t") out += "\\t";
    else if (character === "\n") out += "\\n";
    else if (character === "\r") out += "\\r";
    else if (character !== " " && NON_PRINTABLE.test(character)) {
      if (code < 0x100) out += `\\x${code.toString(16).padStart(2, "0")}`;
      else if (code < 0x10000) out += `\\u${code.toString(16).padStart(4, "0")}`;
      else out += `\\U${code.toString(16).padStart(8, "0")}`;
    } else out += character;
  }
  return out + quote;
}

/** Python's repr() of a float: shortest round-trip digits, its own exponent rules. */
function floatRepr(value: number): string {
  if (Number.isNaN(value)) return "nan";
  if (!Number.isFinite(value)) return value > 0 ? "inf" : "-inf";
  if (value === 0) return Object.is(value, -0) ? "-0.0" : "0.0";
  const [mantissa = "", exponentText = "0"] = value.toExponential().split("e");
  const exponent = Number(exponentText);
  const negative = mantissa.startsWith("-");
  const digits = mantissa.replace("-", "").replace(".", "");
  const sign = negative ? "-" : "";
  if (exponent < -4 || exponent >= 16) {
    const fraction = digits.length > 1 ? `${digits[0]}.${digits.slice(1)}` : digits;
    const power = `${exponent < 0 ? "-" : "+"}${String(Math.abs(exponent)).padStart(2, "0")}`;
    return `${sign}${fraction}e${power}`;
  }
  if (exponent < 0) return `${sign}0.${"0".repeat(-exponent - 1)}${digits}`;
  const whole = digits.padEnd(exponent + 1, "0");
  const fraction = digits.slice(exponent + 1);
  return `${sign}${whole.slice(0, exponent + 1)}.${fraction || "0"}`;
}

/**
 * `repr()` of a value decoded from JSON, for error messages that quote it the
 * way the Python implementation did. A JSON number with no fraction is read as
 * an int, which is what Python's json module does with it.
 */
export function repr(value: unknown): string {
  if (value === null || value === undefined) return "None";
  if (typeof value === "boolean") return value ? "True" : "False";
  if (typeof value === "string") return stringRepr(value);
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : floatRepr(value);
  if (Array.isArray(value)) return `[${value.map(repr).join(", ")}]`;
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    return `{${entries.map(([key, item]) => `${stringRepr(key)}: ${repr(item)}`).join(", ")}}`;
  }
  return String(value);
}
